<template>
  <div class="finance-proceeding-page">
    <div class="table-container">
      <div class="table-header">
        <h3>Finance Monitoring</h3>
        <div class="header-actions">
          <!-- New Payment Ref 按钮 -->
          <el-button 
            type="primary" 
            size="small"
            @click="showPaymentRefDialog = true"
            style="font-weight: 600;"
          >
            <el-icon><Plus /></el-icon>
            New Payment Ref
          </el-button>

          <!-- Payment Ref 选择框 -->
          <div class="payment-ref-select-wrapper">
            <el-select
              v-model="selectedPaymentRefId"
              placeholder="Select Payment Ref"
              size="small"
              style="width: 200px;"
              filterable
              clearable
              @change="onPaymentRefSelect"
            >
              <el-option
                v-for="item in availablePaymentRefsForSelect"
                :key="item.id"
                :label="item.payment_reference + ' (' + item.ref_amt + ')'"
                :value="item.id"
              />
            </el-select>
            <el-button
              type="warning"
              size="small"
              @click="handleCompletePaymentRef"
              :disabled="!selectedPaymentRefId || completingPaymentRef"
              style="font-size:11px;padding:2px 8px;height:28px;"
            >
              {{ completingPaymentRef ? '处理中...' : 'Complete' }}
            </el-button>
          </div>

          <el-popover placement="bottom-end" width="auto" trigger="click">
            <template #reference>
              <el-button size="small" type="primary" plain>
                <el-icon><Setting /></el-icon> Row Setting
              </el-button>
            </template>
            <div class="column-setting-horizontal">
              <div
                v-for="col in visibleColumns"
                :key="col.prop"
                class="column-setting-item-horizontal"
              >
                <el-checkbox
                  v-model="col.visible"
                  :label="col.label"
                  size="small"
                />
              </div>
            </div>
          </el-popover>

          <el-button size="small" type="success" plain @click="loadAllData">
            <el-icon><Refresh /></el-icon> Refresh
          </el-button>
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- 表格: Finance Monitoring List -->
      <!-- ============================================================ -->
      <div class="table-section">
        <div class="table-section-header">
          <h4>Finance Monitoring List</h4>
          <span style="font-size:12px;color:#909399;">
            共 {{ sortedData.length }} 条记录
          </span>
        </div>

        <div class="table-wrapper">
          <el-table
            :data="sortedData"
            border
            style="width:100%"
            v-loading="loading"
            row-key="orderNo"
            :row-style="{ height: 'auto' }"
            :cell-style="{ padding: '2px 4px' }"
            size="small"
          >
            <!-- No -->
            <el-table-column type="index" label="No" width="30" align="center" />

            <!-- Operator - 只读 -->
            <el-table-column
              prop="operator"
              label="Operator"
              width="80"
              v-if="getColumnVisible('operator')"
            >
              <template #default="{ row }">
                <span class="readonly-text">{{ row.operator || '-' }}</span>
              </template>
            </el-table-column>

            <!-- Date - 只读 -->
            <el-table-column
              prop="completeDate"
              label="Date"
              width="90"
              v-if="getColumnVisible('completeDate')"
            >
              <template #default="{ row }">
                <span class="readonly-text">{{ formatDateOnly(row.completeDate) }}</span>
              </template>
            </el-table-column>

            <!-- Order No - 只读，黑色粗体 -->
            <el-table-column
              prop="orderNo"
              label="Order No"
              width="90"
              v-if="getColumnVisible('orderNo')"
            >
              <template #default="{ row }">
                <span class="order-no-text">{{ row.orderNo }}</span>
              </template>
            </el-table-column>

            <!-- Customer - 只读，靠左 -->
            <el-table-column
              prop="customer"
              label="Customer"
              width="100"
              v-if="getColumnVisible('customer')"
            >
              <template #default="{ row }">
                <span class="readonly-text cell-left">{{ row.customer }}</span>
              </template>
            </el-table-column>

            <!-- Vessel - 只读，靠左 -->
            <el-table-column
              prop="vessel"
              label="Vessel"
              width="140"
              v-if="getColumnVisible('vessel')"
            >
              <template #default="{ row }">
                <span class="readonly-text cell-left">{{ row.vessel }}</span>
              </template>
            </el-table-column>

            <!-- Port - 只读，靠左 -->
            <el-table-column
              prop="port"
              label="Port"
              width="110"
              v-if="getColumnVisible('port')"
            >
              <template #default="{ row }">
                <span class="readonly-text cell-left">{{ row.port }}</span>
              </template>
            </el-table-column>

            <!-- SPRO Amt - 只读 -->
            <el-table-column
              prop="sproAmt"
              label="SPRO Amt"
              width="80"
              v-if="getColumnVisible('sproAmt')"
            >
              <template #default="{ row }">
                <span class="readonly-text amount-text">{{ row.sproAmt }}</span>
              </template>
            </el-table-column>

            <!-- Sludge Amt & SQty - 只读 -->
            <el-table-column
              prop="sludgeAmt"
              label="Sludge Amt &amp; SQty"
              width="130"
              v-if="getColumnVisible('sludgeAmt')"
            >
              <template #default="{ row }">
                <div class="merged-cell-two">
                  <span class="readonly-text amount-text">{{ row.sludgeAmt }}</span>
                  <span class="readonly-text qty-text">{{ row.sludgeQty || 0 }}</span>
                </div>
              </template>
            </el-table-column>

            <!-- Garbage Amt & GQty - 只读 -->
            <el-table-column
              prop="garbageAmt"
              label="Garbage Amt &amp; GQty"
              width="130"
              v-if="getColumnVisible('garbageAmt')"
            >
              <template #default="{ row }">
                <div class="merged-cell-two">
                  <span class="readonly-text amount-text">{{ row.garbageAmt }}</span>
                  <span class="readonly-text qty-text">{{ row.garbageQty || 0 }}</span>
                </div>
              </template>
            </el-table-column>

            <!-- MC Amt - 只读 -->
            <el-table-column
              prop="mcAmt"
              label="MC Amt"
              width="70"
              v-if="getColumnVisible('mcAmt')"
            >
              <template #default="{ row }">
                <span class="readonly-text amount-text">{{ row.mcAmt }}</span>
              </template>
            </el-table-column>

            <!-- Others Amt - 只读 -->
            <el-table-column
              prop="othersAmt"
              label="Others Amt"
              width="70"
              v-if="getColumnVisible('othersAmt')"
            >
              <template #default="{ row }">
                <span class="readonly-text amount-text">{{ row.othersAmt }}</span>
              </template>
            </el-table-column>

            <!-- INV No - 黑色粗体，只读 -->
            <el-table-column
              prop="invNo"
              label="INV No"
              width="90"
              v-if="getColumnVisible('invNo')"
              align="center"
            >
              <template #default="{ row }">
                <span class="inv-no-text">{{ row.invNo }}</span>
              </template>
            </el-table-column>

            <!-- INV Amt - 只读 -->
            <el-table-column
              prop="invAmount"
              label="INV Amt"
              width="70"
              v-if="getColumnVisible('invAmount')"
            >
              <template #default="{ row }">
                <span class="readonly-text inv-amount-text">{{ row.invAmount }}</span>
              </template>
            </el-table-column>

            <!-- ⚠️ Payment Date - 只有 confirmed 状态才可编辑，submitted 显示只读 -->
            <el-table-column
              prop="paymentDate"
              label="Payment Date"
              width="100"
              v-if="getColumnVisible('paymentDate')"
            >
              <template #default="{ row }">
                <!-- confirmed 状态：可编辑 -->
                <template v-if="row.status === 'confirmed' && row.source === 'finance_monitoring'">
                  <el-date-picker
                    v-model="row.paymentDate"
                    type="date"
                    size="small"
                    placeholder="选择日期"
                    format="YYYY-MM-DD"
                    value-format="YYYY-MM-DD"
                    style="width:100%"
                    @change="handleFieldChange(row)"
                    clearable
                  />
                </template>
                <!-- submitted 状态：只读 -->
                <template v-else>
                  <span class="readonly-text">{{ row.paymentDate || '-' }}</span>
                </template>
              </template>
            </el-table-column>

            <!-- ⚠️ Payment Ref - 只有 confirmed 状态才可编辑，submitted 显示只读 -->
            <el-table-column
              prop="paymentRef"
              label="Payment Ref"
              width="130"
              v-if="getColumnVisible('paymentRef')"
            >
              <template #default="{ row }">
                <!-- confirmed 状态：可编辑 -->
                <template v-if="row.status === 'confirmed' && row.source === 'finance_monitoring'">
                  <el-select
                    v-model="row.paymentRefId"
                    size="small"
                    placeholder="Select Payment Ref"
                    style="width:100%"
                    @change="handlePaymentRefChange(row)"
                    filterable
                    clearable
                  >
                    <el-option
                      v-for="item in availablePaymentRefs"
                      :key="item.id"
                      :label="item.payment_reference + ' (' + item.ref_amt + ')'"
                      :value="item.id"
                    />
                  </el-select>
                </template>
                <!-- submitted 状态：只读 -->
                <template v-else>
                  <span class="readonly-text">{{ row.paymentRef || '-' }}</span>
                </template>
              </template>
            </el-table-column>

            <!-- Ref Amt - 只读 -->
            <el-table-column
              prop="refAmt"
              label="Ref Amt"
              width="90"
              v-if="getColumnVisible('refAmt')"
            >
              <template #default="{ row }">
                <span class="readonly-text ref-amt-text">{{ row.refAmt || 0 }}</span>
              </template>
            </el-table-column>

            <!-- T INV Amt - 只读 -->
            <el-table-column
              prop="tInvAmt"
              label="T INV Amt"
              width="90"
              v-if="getColumnVisible('tInvAmt')"
            >
              <template #default="{ row }">
                <span class="readonly-text t-inv-amt-text">{{ calculateTInvAmt(row) }}</span>
              </template>
            </el-table-column>

            <!-- ⚠️ Finance Remarks - 只有 confirmed 状态才可编辑，submitted 显示只读 -->
            <el-table-column
              prop="finRemarks"
              label="Finance Remarks"
              width="140"
              v-if="getColumnVisible('finRemarks')"
            >
              <template #default="{ row }">
                <!-- confirmed 状态：可编辑 -->
                <template v-if="row.status === 'confirmed' && row.source === 'finance_monitoring'">
                  <el-input
                    v-model="row.finRemarks"
                    size="small"
                    placeholder="Financial Remarks"
                    @input="handleFieldChange(row)"
                    clearable
                  />
                </template>
                <!-- submitted 状态：只读 -->
                <template v-else>
                  <span class="readonly-text">{{ row.finRemarks || '-' }}</span>
                </template>
              </template>
            </el-table-column>

            <!-- Ops Remarks - 只读，靠左 -->
            <el-table-column
              prop="opsRemarks"
              label="Ops Remarks"
              width="140"
              v-if="getColumnVisible('opsRemarks')"
            >
              <template #default="{ row }">
                <span class="readonly-text cell-left">{{ row.opsRemarks || '-' }}</span>
              </template>
            </el-table-column>

            <!-- Action -->
            <el-table-column 
              label="Action" 
              min-width="160"
            >
              <template #default="{ row }">
                <div class="action-buttons">
                  <!-- submitted 状态：显示 Reject 和 Confirm -->
                  <template v-if="row.status === 'submitted'">
                    <el-button
                      type="danger"
                      size="small"
                      @click="handleReject(row)"
                      style="font-size:10px;padding:2px 6px;height:20px;"
                    >
                      Reject
                    </el-button>
                    <el-button
                      type="success"
                      size="small"
                      @click="handleConfirm(row)"
                      style="font-size:10px;padding:2px 6px;height:20px;"
                    >
                      Confirm
                    </el-button>
                  </template>
                  <!-- confirmed 状态：显示 Complete 按钮 -->
                  <template v-else-if="row.status === 'confirmed'">
                    <el-button
                      type="warning"
                      size="small"
                      @click="handleComplete(row)"
                      style="font-size:10px;padding:2px 6px;height:20px;"
                    >
                      Complete
                    </el-button>
                  </template>
                  <!-- completed 状态：显示标签 -->
                  <template v-else-if="row.status === 'completed'">
                    <span class="status-tag status-completed">Completed</span>
                  </template>
                  <template v-else>
                    <span class="status-tag">{{ row.status }}</span>
                  </template>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- New Payment Ref Dialog -->
    <!-- ============================================================ -->
    <el-dialog
      v-model="showPaymentRefDialog"
      title="New Payment Ref"
      width="500px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <el-form :model="paymentRefForm" label-width="120px" :rules="paymentRefRules" ref="paymentRefFormRef">
        <el-form-item label="Payment Date" prop="payment_date">
          <el-date-picker
            v-model="paymentRefForm.payment_date"
            type="date"
            placeholder="Choose Date"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="Payment Reference" prop="payment_reference">
          <el-input v-model="paymentRefForm.payment_reference" placeholder="Please enter payment reference" />
        </el-form-item>
        <el-form-item label="Ref Amt" prop="ref_amt">
          <el-input-number
            v-model="paymentRefForm.ref_amt"
            :precision="2"
            :step="100"
            :min="0"
            placeholder="Please enter reference amount"
            style="width:100%"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showPaymentRefDialog = false">Cancel</el-button>
        <el-button type="primary" @click="submitPaymentRef" :loading="submittingPaymentRef">
          Submit
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Setting, Refresh, Plus } from '@element-plus/icons-vue'

// ============ 状态 ============
const loading = ref(false)
const submittingPaymentRef = ref(false)
const completingPaymentRef = ref(false)

const financeList = ref([])
const sortedData = ref([])
const paymentRefList = ref([])
const selectedPaymentRefId = ref(null)
const showPaymentRefDialog = ref(false)
const paymentRefFormRef = ref(null)
let updateTimer = null
let isSubmitting = false

// ============================================================
// Payment Ref 表单
// ============================================================
const paymentRefForm = ref({
  payment_date: '',
  payment_reference: '',
  ref_amt: 0
})

const paymentRefRules = {
  payment_date: [{ required: true, message: 'Please choose payment date', trigger: 'change' }],
  payment_reference: [{ required: true, message: 'Please enter payment reference', trigger: 'blur' }],
  ref_amt: [{ required: true, message: 'Please enter reference amount', trigger: 'blur' }]
}

// ============================================================
// 计算属性 - 可用的付款参考（未确认的）
// ============================================================
const availablePaymentRefs = computed(() => {
  return paymentRefList.value.filter(item => item.status !== 'confirmed')
})

const availablePaymentRefsForSelect = computed(() => {
  return paymentRefList.value.filter(item => item.status !== 'confirmed')
})

// ============================================================
// 列配置（Finance Monitoring）
// ============================================================
const STORAGE_KEY = 'finance_monitoring_columns'

const defaultColumns = [
  { prop: 'operator', label: 'Operator', visible: true },
  { prop: 'completeDate', label: 'Date', visible: true },
  { prop: 'orderNo', label: 'Order No', visible: true },
  { prop: 'customer', label: 'Customer', visible: true },
  { prop: 'vessel', label: 'Vessel', visible: true },
  { prop: 'port', label: 'Port', visible: true },
  { prop: 'sproAmt', label: 'SPRO Amt', visible: true },
  { prop: 'sludgeAmt', label: 'Sludge Amt & SQty', visible: true },
  { prop: 'garbageAmt', label: 'Garbage Amt & GQty', visible: true },
  { prop: 'mcAmt', label: 'MC Amt', visible: true },
  { prop: 'othersAmt', label: 'Others Amt', visible: true },
  { prop: 'invNo', label: 'INV No', visible: true },
  { prop: 'invAmount', label: 'INV Amt', visible: true },
  { prop: 'paymentDate', label: 'Payment Date', visible: true },
  { prop: 'paymentRef', label: 'Payment Ref', visible: true },
  { prop: 'refAmt', label: 'Ref Amt', visible: true },
  { prop: 'tInvAmt', label: 'T INV Amt', visible: true },
  { prop: 'finRemarks', label: 'Finance Remarks', visible: true },
  { prop: 'opsRemarks', label: 'Ops Remarks', visible: true }
]

const loadColumnsFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      return defaultColumns.map(col => {
        const found = parsed.find(p => p.prop === col.prop)
        return found ? { ...col, visible: found.visible } : col
      })
    }
  } catch (e) {
    console.warn('读取列配置失败:', e)
  }
  return defaultColumns
}

const columns = ref(loadColumnsFromStorage())

const visibleColumns = computed(() => columns.value)

const getColumnVisible = (prop) => {
  const col = columns.value.find(c => c.prop === prop)
  return col ? col.visible : true
}

const saveColumnsToStorage = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columns.value))
  } catch (e) {
    console.warn('保存列配置失败:', e)
  }
}

watch(columns, saveColumnsToStorage, { deep: true })

// ============================================================
// API 配置
// ============================================================
const getApiBase = () => process.env.VUE_APP_API_URL || 'http://127.0.0.1:3000'

const getHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  }
}

// ============================================================
// 工具函数
// ============================================================
const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const formatDateOnly = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// ✅ 格式化日期为 YYYY-MM-DD
const formatDateToYYYYMMDD = (dateStr) => {
  if (!dateStr) return null
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return null
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// ============================================================
// ✅ 数据映射 - 兼容 order_monitoring 和 finance_monitoring
// ============================================================
const mapDbToFrontend = (dbData) => {
  const isFromOrderMonitoring = dbData.source === 'order_monitoring' || dbData.source === undefined;
  
  return {
    id: dbData.id,
    operator: dbData.operator || '',
    orderNo: dbData.order_no || '',
    completeDate: isFromOrderMonitoring 
      ? formatDate(dbData.created_at) 
      : formatDate(dbData.confirmed_at || dbData.created_at),
    customer: dbData.customer || '',
    vessel: dbData.vessel || '',
    port: dbData.port || '',
    sproAmt: dbData.spro_amt ?? 0,
    sludgeAmt: dbData.sludge_amt ?? 0,
    sludgeQty: dbData.s_qty ?? 0,
    garbageAmt: dbData.garbage_amt ?? 0,
    garbageQty: dbData.g_qty ?? 0,
    mcAmt: dbData.mc_amt ?? 0,
    othersAmt: dbData.others_amt ?? 0,
    invNo: dbData.inv_no || '',
    invAmount: dbData.inv_amount ?? 0,
    paymentDate: dbData.payment_date ? formatDateToYYYYMMDD(dbData.payment_date) : '',
    paymentRefId: dbData.payment_ref_id || null,
    paymentRef: dbData.payment_ref || '',
    refAmt: dbData.ref_amt ?? 0,
    finRemarks: dbData.fin_remarks || '',
    opsRemarks: dbData.ops_remarks || '',
    status: dbData.status || 'submitted',
    source: dbData.source || 'order_monitoring'
  }
}

// ============================================================
// ✅ 获取数据
// ============================================================
const fetchFinanceOrders = async () => {
  try {
    const response = await fetch(`${getApiBase()}/api/auth/finance-monitoring`, {
      headers: getHeaders()
    })
    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return []
    }
    if (!response.ok) throw new Error('获取财务列表失败')
    const result = await response.json()
    console.log('✅ Finance Monitoring 数据:', result.data)
    return result.data || []
  } catch (error) {
    console.error('获取财务列表错误:', error)
    throw error
  }
}

const fetchPaymentRefs = async () => {
  try {
    console.log('📡 正在获取付款参考列表...')
    const response = await fetch(`${getApiBase()}/api/auth/payment-ref`, {
      headers: getHeaders()
    })
    
    if (!response.ok) {
      let errorDetail = ''
      try {
        const errorData = await response.json()
        errorDetail = errorData.message || JSON.stringify(errorData)
      } catch (e) {
        errorDetail = await response.text()
      }
      console.error('❌ Payment Ref 接口错误:', response.status, errorDetail)
      throw new Error(`获取付款参考失败 (${response.status}): ${errorDetail}`)
    }
    
    const result = await response.json()
    console.log('✅ Payment Ref 数据:', result)
    paymentRefList.value = result.data || []
    
    if (selectedPaymentRefId.value) {
      const stillExists = paymentRefList.value.some(item => 
        item.id === selectedPaymentRefId.value && item.status !== 'confirmed'
      )
      if (!stillExists) {
        selectedPaymentRefId.value = null
      }
    }
  } catch (error) {
    console.error('❌ 获取付款参考错误:', error)
    if (paymentRefList.value.length === 0) {
      ElMessage.warning('付款参考列表加载失败，请检查后端服务')
    }
    throw error
  }
}

const loadAllData = async () => {
  if (loading.value) return
  loading.value = true
  try {
    try {
      await fetchPaymentRefs()
    } catch (error) {
      console.warn('⚠️ 付款参考加载失败，继续加载主数据:', error)
    }
    
    const financeData = await fetchFinanceOrders()
    financeList.value = financeData.map(mapDbToFrontend)
    sortedData.value = financeList.value.sort((a, b) => {
      const order = { 'submitted': 0, 'confirmed': 1, 'completed': 2 }
      return (order[a.status] || 0) - (order[b.status] || 0)
    })
    console.log('sortedData 加载完成:', sortedData.value)
  } catch (error) {
    console.error('❌ 加载数据失败:', error)
    ElMessage.error('加载数据失败')
  } finally {
    loading.value = false
  }
}

// ============================================================
// 计算 T INV Amt
// ============================================================
const calculateTInvAmt = (row) => {
  const invAmt = parseFloat(row.invAmount) || 0
  const refAmt = parseFloat(row.refAmt) || 0
  const result = invAmt - refAmt
  return result.toFixed(2)
}

// ============================================================
// ✅ 字段变更处理 - 只有 confirmed 状态才保存
// ============================================================
const handleFieldChange = (row) => {
  // ✅ 只有 confirmed 状态且来源是 finance_monitoring 才保存
  if (row.status !== 'confirmed' || row.source !== 'finance_monitoring') {
    console.log('⏭️ 跳过保存，状态:', row.status, '来源:', row.source)
    return
  }
  
  if (updateTimer) {
    clearTimeout(updateTimer)
  }
  updateTimer = setTimeout(async () => {
    await saveRowToDatabase(row)
    updateTimer = null
  }, 500)
}

const handlePaymentRefChange = (row) => {
  // ✅ 只有 confirmed 状态且来源是 finance_monitoring 才处理
  if (row.status !== 'confirmed' || row.source !== 'finance_monitoring') {
    console.log('⏭️ 跳过付款参考变更，状态:', row.status, '来源:', row.source)
    return
  }
  
  const selected = paymentRefList.value.find(item => item.id === row.paymentRefId)
  if (selected) {
    row.refAmt = selected.ref_amt || 0
    row.paymentRef = selected.payment_reference || ''
    // ✅ 格式化日期为 YYYY-MM-DD
    if (selected.payment_date) {
      row.paymentDate = formatDateToYYYYMMDD(selected.payment_date)
    }
  } else {
    row.refAmt = 0
    row.paymentRef = ''
  }
  handleFieldChange(row)
}

// ✅ 保存到 finance_monitoring 表（只有 confirmed 状态才保存）
const saveRowToDatabase = async (row) => {
  // ✅ 只有 confirmed 状态且来源是 finance_monitoring 才保存
  if (row.status !== 'confirmed' || row.source !== 'finance_monitoring') {
    console.log('⏭️ 跳过保存，状态:', row.status, '来源:', row.source)
    return
  }
  
  try {
    // ✅ 格式化日期为 YYYY-MM-DD
    const formattedPaymentDate = formatDateToYYYYMMDD(row.paymentDate)
    
    console.log('💾 正在保存财务数据:', {
      orderNo: row.orderNo,
      finRemarks: row.finRemarks,
      paymentDate: formattedPaymentDate,
      paymentRefId: row.paymentRefId
    })
    
    const response = await fetch(`${getApiBase()}/api/auth/finance-monitoring/${row.orderNo}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({
        fin_remarks: row.finRemarks || null,
        payment_date: formattedPaymentDate,
        payment_ref_id: row.paymentRefId || null
      })
    })
    
    if (response.status === 401) {
      ElMessage.error('Authentication has expired. Please log in again.')
      setTimeout(() => window.location.href = '/login', 1500)
      return
    }
    
    if (!response.ok) {
      let errorDetail = ''
      try {
        const errorData = await response.json()
        errorDetail = errorData.message || JSON.stringify(errorData)
      } catch (e) {
        errorDetail = await response.text()
      }
      console.error('❌ 保存失败详情:', errorDetail)
      throw new Error(`保存失败 (${response.status}): ${errorDetail}`)
    }
    
    const result = await response.json()
    console.log('✅ 保存成功:', result)
    
    if (!result.success) {
      ElMessage.error(result.message || '保存失败')
    }
  } catch (error) {
    console.error('❌ 保存失败:', error)
    ElMessage.error(`保存失败: ${error.message}`)
  }
}

// ============================================================
// Payment Ref 选择
// ============================================================
const onPaymentRefSelect = (value) => {
  console.log('选择付款参考:', value)
}

// ============================================================
// Complete Payment Ref
// ============================================================
const handleCompletePaymentRef = async () => {
  if (!selectedPaymentRefId.value) {
    ElMessage.warning('请先选择付款参考')
    return
  }

  try {
    await ElMessageBox.confirm(
      `Are you sure you want to complete the selected payment reference?\nOnce completed, this payment reference will no longer be available.`,
      'Complete Payment Reference',
      {
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }
    )

    completingPaymentRef.value = true

    const response = await fetch(`${getApiBase()}/api/auth/payment-ref/${selectedPaymentRefId.value}/confirm`, {
      method: 'PUT',
      headers: getHeaders()
    })

    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return
    }

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '完成失败')
    }

    const result = await response.json()
    if (result.success) {
      ElMessage.success('付款参考已完成！')
      selectedPaymentRefId.value = null
      await fetchPaymentRefs()
    } else {
      ElMessage.error(result.message || '完成失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(`完成失败: ${error.message}`)
    }
  } finally {
    completingPaymentRef.value = false
  }
}

// ============================================================
// New Payment Ref
// ============================================================
const submitPaymentRef = async () => {
  if (!paymentRefFormRef.value) return
  
  await paymentRefFormRef.value.validate(async (valid) => {
    if (!valid) return
    
    submittingPaymentRef.value = true
    try {
      const response = await fetch(`${getApiBase()}/api/auth/payment-ref`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          payment_date: paymentRefForm.value.payment_date,
          payment_reference: paymentRefForm.value.payment_reference,
          ref_amt: paymentRefForm.value.ref_amt,
          no: paymentRefForm.value.no || null
        })
      })
      
      if (response.status === 401) {
        ElMessage.error('认证已过期，请重新登录')
        setTimeout(() => window.location.href = '/login', 1500)
        return
      }
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '新增失败')
      }
      
      const result = await response.json()
      if (result.success) {
        ElMessage.success('付款参考新增成功！')
        showPaymentRefDialog.value = false
        paymentRefForm.value = {
          payment_date: '',
          payment_reference: '',
          ref_amt: 0
        }
        await fetchPaymentRefs()
      } else {
        ElMessage.error(result.message || '新增失败')
      }
    } catch (error) {
      ElMessage.error(`新增失败: ${error.message}`)
    } finally {
      submittingPaymentRef.value = false
    }
  })
}

// ============================================================
// ✅ Finance Monitoring - 操作
// ============================================================

// Reject - 拒绝，订单回到 pending（从列表移除）
const handleReject = async (row) => {
  if (row.status !== 'submitted' || isSubmitting) return

  try {
    await ElMessageBox.confirm(
      `确定要拒绝订单 ${row.orderNo} 吗？\n拒绝后订单将返回运营重新编辑。`,
      '拒绝订单',
      {
        confirmButtonText: '确定拒绝',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )

    isSubmitting = true
    loading.value = true

    const response = await fetch(`${getApiBase()}/api/auth/order-monitoring/${row.orderNo}/reject`, {
      method: 'PUT',
      headers: getHeaders()
    })

    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return
    }

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '拒绝失败')
    }

    const result = await response.json()
    if (result.success) {
      ElMessage.warning(`订单 ${row.orderNo} 已拒绝，返回运营重新编辑！`)
      const index = sortedData.value.findIndex(item => item.orderNo === row.orderNo)
      if (index > -1) {
        sortedData.value.splice(index, 1)
      }
    } else {
      ElMessage.error(result.message || '拒绝失败')
    }
    
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(`拒绝失败: ${error.message}`)
    }
  } finally {
    isSubmitting = false
    loading.value = false
  }
}

// Confirm - 确认，写入 finance_monitoring
const handleConfirm = async (row) => {
  if (row.status !== 'submitted' || isSubmitting) return

  try {
    await ElMessageBox.confirm(
      `确定要确认订单 ${row.orderNo} 吗？\n确认后订单将进入财务监控列表，可以选择付款参考。`,
      '确认订单',
      {
        confirmButtonText: '确定确认',
        cancelButtonText: '取消',
        type: 'success'
      }
    )

    isSubmitting = true
    loading.value = true

    const response = await fetch(`${getApiBase()}/api/auth/order-monitoring/${row.orderNo}/confirm`, {
      method: 'PUT',
      headers: getHeaders()
    })

    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return
    }

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '确认失败')
    }

    const result = await response.json()
    if (result.success) {
      ElMessage.success(`订单 ${row.orderNo} 已确认！`)
      const index = sortedData.value.findIndex(item => item.orderNo === row.orderNo)
      if (index > -1) {
        sortedData.value.splice(index, 1)
      }
      await loadAllData()
    } else {
      ElMessage.error(result.message || '确认失败')
    }
    
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(`确认失败: ${error.message}`)
    }
  } finally {
    isSubmitting = false
    loading.value = false
  }
}

// Complete - 完成财务处理（移到列表最下方，不可编辑）
const handleComplete = async (row) => {
  if (row.status !== 'confirmed' || isSubmitting) return

  try {
    await ElMessageBox.confirm(
      `确定要完成订单 ${row.orderNo} 的财务处理吗？\n完成后订单将移到列表最下方，不可再编辑。`,
      '完成财务处理',
      {
        confirmButtonText: '确定完成',
        cancelButtonText: '取消',
        type: 'success'
      }
    )

    isSubmitting = true
    loading.value = true

    await saveRowToDatabase(row)

    const response = await fetch(`${getApiBase()}/api/auth/finance-monitoring/${row.orderNo}/complete`, {
      method: 'PUT',
      headers: getHeaders()
    })

    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return
    }

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '完成失败')
    }

    const result = await response.json()
    if (result.success) {
      ElMessage.success(`订单 ${row.orderNo} 财务处理已完成！`)
      row.status = 'completed'
      const order = { 'submitted': 0, 'confirmed': 1, 'completed': 2 }
      sortedData.value.sort((a, b) => {
        return (order[a.status] || 0) - (order[b.status] || 0)
      })
    } else {
      ElMessage.error(result.message || '完成失败')
    }
    
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(`完成失败: ${error.message}`)
    }
  } finally {
    isSubmitting = false
    loading.value = false
  }
}

// ============================================================
// 生命周期
// ============================================================
onMounted(() => {
  loadAllData()
})

onBeforeUnmount(() => {
  if (updateTimer) {
    clearTimeout(updateTimer)
    updateTimer = null
  }
})
</script>

<style scoped>
.finance-proceeding-page {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.table-container {
  flex: 1;
  overflow: auto;
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #ebeef5;
}

.table-header h3 {
  margin: 0;
  font-size: 18px;
  color: #303133;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.payment-ref-select-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
}

.payment-ref-select-wrapper .el-select {
  width: 200px !important;
}

.payment-ref-select-wrapper .el-select .el-input__wrapper {
  height: 28px !important;
  min-height: 28px !important;
}

.payment-ref-select-wrapper .el-select .el-input__inner {
  font-size: 12px !important;
  height: 26px !important;
  line-height: 26px !important;
}

.table-section {
  margin-bottom: 16px;
}

.table-section:last-child {
  margin-bottom: 0;
}

.table-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #e8ecf1;
}

.table-section-header h4 {
  margin: 0;
  font-size: 14px;
  color: #303133;
  font-weight: 600;
}

.table-wrapper {
  overflow: auto;
  max-height: 500px;
}

.column-setting-horizontal {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  padding: 12px 16px;
  max-width: 600px;
  min-width: 300px;
}

.column-setting-item-horizontal {
  flex: 0 0 auto;
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 3px;
  align-items: center;
  flex-wrap: nowrap;
  justify-content: center;
}

:deep(.el-table) {
  font-size: 13px;
}

:deep(.el-table .cell) {
  padding: 2px 4px;
}

:deep(.el-table td.el-table__cell) {
  padding: 2px 0 !important;
}

/* No 列居中 */
:deep(.el-table .el-table-column--selection .cell),
:deep(.el-table td .cell) {
  text-align: center;
}

:deep(.el-table__header-wrapper) {
  background: #3498db !important;
}

:deep(.el-table th.el-table__cell) {
  background-color: #3498db !important;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 11px !important;
  padding: 4px 0 !important;
}

:deep(.el-table th.el-table__cell .cell) {
  font-size: 11px !important;
  line-height: 20px !important;
  color: #ffffff !important;
  white-space: normal;
  word-break: break-word;
  text-align: center;
}

:deep(.el-table__body-wrapper) {
  overflow-y: auto;
}

/* ✅ 只读文本样式 */
.readonly-text {
  display: inline-block;
  padding: 2px 6px;
  color: #606266;
  font-weight: 400;
  font-size: 12px;
  background: #f5f7fa;
  border-radius: 3px;
  width: 100%;
  text-align: center;
}

/* ✅ Order No - 黑色粗体 */
.order-no-text {
  display: inline-block;
  padding: 2px 6px;
  color: #303133;
  font-weight: 700;
  font-size: 12px;
  background: #f5f7fa;
  border-radius: 3px;
  width: 100%;
  text-align: center;
}

/* ✅ INV No - 黑色粗体 */
.inv-no-text {
  display: inline-block;
  padding: 2px 6px;
  color: #303133;
  font-weight: 700;
  font-size: 12px;
  background: #f5f7fa;
  border-radius: 3px;
  width: 100%;
  text-align: center;
}

.cell-left {
  text-align: left !important;
  padding-left: 8px !important;
}

.amount-text {
  color: #409eff;
  font-weight: 500;
  background: #ecf5ff;
}

.inv-amount-text {
  color: #67c23a;
  font-weight: 700;
  background: #f0f9eb;
}

.ref-amt-text {
  color: #e6a23c;
  font-weight: 600;
  background: #fdf6ec;
}

.t-inv-amt-text {
  color: #f56c6c;
  font-weight: 700;
  background: #fef0f0;
}

.qty-text {
  color: #909399;
  font-weight: 400;
  background: #f5f7fa;
  font-size: 11px;
}

.merged-cell-two {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.merged-cell-two .readonly-text {
  flex: 1;
}

.status-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  background: #e8ecf1;
  color: #606266;
}

.status-completed {
  background: #f0f9eb;
  color: #67c23a;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a8b0b8;
}

/* ✅ 日期选择器样式 */
:deep(.el-date-editor) {
  width: 100% !important;
  height: 28px !important;
}

:deep(.el-date-editor .el-input__wrapper) {
  padding: 0 4px !important;
  height: 28px !important;
  min-height: 28px !important;
}

:deep(.el-date-editor .el-input__inner) {
  font-size: 12px !important;
  height: 26px !important;
  line-height: 26px !important;
}

/* ✅ Select 样式 */
:deep(.el-select .el-input__wrapper) {
  padding: 0 4px !important;
  height: 28px !important;
  min-height: 28px !important;
}

:deep(.el-select .el-input__inner) {
  font-size: 12px !important;
  height: 26px !important;
  line-height: 26px !important;
}

/* Dialog 样式 */
:deep(.el-dialog) {
  border-radius: 12px;
}

:deep(.el-dialog__header) {
  border-bottom: 1px solid #ebeef5;
  padding: 16px 20px;
}

:deep(.el-dialog__body) {
  padding: 20px;
}

:deep(.el-dialog__footer) {
  border-top: 1px solid #ebeef5;
  padding: 12px 20px;
}
</style>